import React, { useEffect, useState } from 'react';
import CompetitiveIntelligenceReport from '../../../components/CompetitiveIntelligenceReport';
import CompetitorChipPicker from '../../../components/CompetitorChipPicker';
import { getCompetitiveIntelligenceReport, getMe } from '../../../api';
import { BarChart3, Clock, AlertTriangle, List, History, ShieldCheck, TrendingUp, Smile, ChevronRight, ArrowLeft } from 'lucide-react';
const Analytics = () => {
  const [selectedId, setSelectedId] = useState(''); // starts with empty string to show 4x2 grid of cards
  const [competitorTags, setCompetitorTags] = useState(['Pacific Life', 'Nationwide']);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState('');
  const [subjectClientName, setSubjectClientName] = useState('');
  const [simulating, setSimulating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const templates = [{
    id: 'competitive_intelligence',
    label: 'Competitive Intelligence',
    sub: 'Market Share & Snapshot Comparison',
    badge: 'ACTIVE',
    badgeColor: 'var(--c-neon)',
    // Brand Purple
    badgeBg: 'rgba(116, 86, 218, 0.08)',
    icon: 'BarChart3',
    desc: 'Compare state-by-state carrier filings, rates, and objections in real-time.',
    graphicType: 'bars'
  }, {
    id: 'doi_approval_speed',
    label: 'State DOI Approval Speed',
    sub: 'Lead-Time & Review Durations',
    badge: '94% ACCURACY',
    badgeColor: 'var(--text2)',
    // Brand Green
    badgeBg: 'rgba(14, 112, 47, 0.08)',
    icon: 'Clock',
    desc: 'Predict review lead times and approval durations across different state examiners.',
    graphicType: 'line'
  }, {
    id: 'objection_clause_clusters',
    label: 'Objection Clause Clusters',
    sub: 'NLP Objection Parsing',
    badge: 'AI POWERED',
    badgeColor: 'var(--text2)',
    // Brand Red
    badgeBg: 'rgba(195, 70, 94, 0.08)',
    icon: 'AlertTriangle',
    desc: 'Identify common objection themes, clause groups, and pre-approved response matching.',
    graphicType: 'dots'
  }, {
    id: 'product_line_penetration',
    label: 'Product Line Penetration',
    sub: 'TOI Distribution Tracker',
    badge: 'REGIONAL',
    badgeColor: 'var(--c-black)',
    // Brand Blue
    badgeBg: 'rgba(20, 110, 246, 0.08)',
    icon: 'List',
    desc: 'Track regional market expansion and filing volume across 19.0 Personal Auto and Commercial.',
    graphicType: 'pie'
  }, {
    id: 'rate_change_variance',
    label: 'Rate Change Variance',
    sub: 'Competitor Rate Impact Analysis',
    badge: 'HISTORICAL',
    badgeColor: 'var(--c-neon)',
    // Brand Purple
    badgeBg: 'rgba(116, 86, 218, 0.08)',
    icon: 'History',
    desc: 'Analyze competitor rate adjustments, historical timeline filings, and average impact ratios.',
    graphicType: 'area'
  }, {
    id: 'regulatory_compliance_score',
    label: 'Regulatory Compliance Score',
    sub: 'Automated Guidelines Audit',
    badge: 'COMPLIANT',
    badgeColor: 'var(--text2)',
    // Brand Green
    badgeBg: 'rgba(14, 112, 47, 0.08)',
    icon: 'ShieldCheck',
    desc: 'Audit new filings automatically against updated state statutes and SERFF guidelines.',
    graphicType: 'progress'
  }, {
    id: 'loss_cost_multipliers',
    label: 'Loss Cost Multipliers (LCM)',
    sub: 'Underwriting Margin Evaluation',
    badge: 'STRATEGIC',
    badgeColor: 'var(--text2)',
    // Brand Red
    badgeBg: 'rgba(195, 70, 94, 0.08)',
    icon: 'TrendingUp',
    desc: 'Assess competitor underwriting margins, base rates, and state filings multiplier updates.',
    graphicType: 'dual-bars'
  }, {
    id: 'examiner_feedback_sentiment',
    label: 'Examiner Feedback Sentiment',
    sub: 'Examiner Correspondence Analysis',
    badge: 'PREDICTIVE',
    badgeColor: 'var(--c-black)',
    // Brand Blue
    badgeBg: 'rgba(20, 110, 246, 0.08)',
    icon: 'Smile',
    desc: 'Score examiner correspondence sentiment to optimize response times and approval rates.',
    graphicType: 'slider'
  }];
  useEffect(() => {
    getMe().then(res => {
      const name = res.data.user?.client?.client_name;
      setSubjectClientName(typeof name === 'string' ? name.trim() : '');
    }).catch(() => setSubjectClientName(''));
  }, []);
  const runReport = async () => {
    setError('');
    if (selectedId !== 'competitive_intelligence') {
      setError('Select an available report type.');
      return;
    }
    setLoadingReport(true);
    try {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      const competitors = competitorTags.map(s => s.trim()).filter(Boolean).join(',');
      if (!competitors.trim()) {
        setError('Add at least one competitor.');
        return;
      }
      const res = await getCompetitiveIntelligenceReport({
        competitors,
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10)
      });
      setReport(res.data.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Request failed');
    } finally {
      setLoadingReport(false);
    }
  };
  const renderIcon = (name, size = 16, color = 'currentColor') => {
    switch (name) {
      case 'BarChart3':
        return <BarChart3 size={size} style={{
          color
        }} />;
      case 'Clock':
        return <Clock size={size} style={{
          color
        }} />;
      case 'AlertTriangle':
        return <AlertTriangle size={size} style={{
          color
        }} />;
      case 'List':
        return <List size={size} style={{
          color
        }} />;
      case 'History':
        return <History size={size} style={{
          color
        }} />;
      case 'ShieldCheck':
        return <ShieldCheck size={size} style={{
          color
        }} />;
      case 'TrendingUp':
        return <TrendingUp size={size} style={{
          color
        }} />;
      case 'Smile':
        return <Smile size={size} style={{
          color
        }} />;
      default:
        return null;
    }
  };
  const renderMicroGraphic = (type, badgeColor) => {
    switch (type) {
      case 'bars':
        return <div className="chart-thumb-bars">
          <div className="chart-thumb-bars__bar"></div>
          <div className="chart-thumb-bars__bar"></div>
          <div className="chart-thumb-bars__bar"></div>
          <div className="chart-thumb-bars__bar"></div>
        </div>;
      case 'line':
        return <svg width="70" height="40" viewBox="0 0 70 40" className="chart-thumb-line">
          <polyline fill="none" stroke={badgeColor} strokeWidth="3" strokeLinecap="round" points="5,35 20,20 35,28 50,10 65,5" />
        </svg>;
      case 'dots':
        return <div className="chart-thumb-dots">
          <div className="chart-thumb-dots__dot"></div>
          <div className="chart-thumb-dots__dot"></div>
          <div className="chart-thumb-dots__dot"></div>
          <div className="chart-thumb-dots__dot"></div>
          <div className="chart-thumb-dots__dot"></div>
        </div>;
      case 'pie':
        return <div className="chart-thumb-pie"></div>;
      case 'area':
        return <svg width="70" height="40" viewBox="0 0 70 40" className="chart-thumb-area">
          <path d="M5,35 L20,15 L35,25 L50,8 L65,5 L65,35 Z" fill="rgba(116, 86, 218, 0.08)" stroke="var(--c-neon)" strokeWidth="2.5" strokeLinecap="round" />
        </svg>;
      case 'progress':
        return <div className="chart-thumb-progress">
          <div className="chart-thumb-progress__labels">
            <span>98%</span>
            <span>AUD</span>
          </div>
          <div className="chart-thumb-progress__track">
            <div className="chart-thumb-progress__fill"></div>
          </div>
        </div>;
      case 'dual-bars':
        return <div className="chart-thumb-dual">
          <div className="chart-thumb-dual__group">
            <div className="chart-thumb-dual__bar"></div>
            <div className="chart-thumb-dual__bar"></div>
          </div>
          <div className="chart-thumb-dual__group">
            <div className="chart-thumb-dual__bar"></div>
            <div className="chart-thumb-dual__bar"></div>
          </div>
        </div>;
      case 'slider':
        return <div className="chart-thumb-sentiment">
          <div className="chart-thumb-sentiment__row">
            <span>Neg</span>
            <span className="chart-thumb-sentiment__label">Pos</span>
          </div>
          <div className="chart-thumb-sentiment__track">
            <div className="chart-thumb-sentiment__fill"></div>
          </div>
        </div>;
      default:
        return null;
    }
  };
  const renderMockWorkspace = id => {
    const template = templates.find(t => t.id === id);
    if (!template) return null;
    return <div className="analytics-workspace">
      <div className="analytics-workspace__header">
        <div>
          <div className="analytics-workspace__badge-row">
            <span style={{
              background: template.badgeBg,
              color: template.badgeColor
            }} className="analytics-workspace__badge">
              {renderIcon(template.icon, 20, template.badgeColor)}
            </span>
            <h2 className="analytics-workspace__title">{template.label} Workspace</h2>
          </div>
          <p className="analytics-workspace__sub">{template.sub} · High-fidelity predictive modeling sandbox</p>
        </div>
        <span style={{
          color: template.badgeColor,
          background: template.badgeBg
        }} className="analytics-workspace__run-btn">
          {template.badge}
        </span>
      </div>

      {/* Filters Bar */}
      <div className="filters-bar analytics-workspace__filters">
        <div className="select-wrap analytics-workspace__select">
          <span className="select-measure analytics-workspace__select-measure">All states</span>
          <select className="filter-select analytics-workspace__select-input">
            <option value="All states">All states</option>
            <option value="ID">ID</option>
            <option value="OK">OK</option>
            <option value="OR">OR</option>
          </select>
        </div>
        <div className="select-wrap analytics-workspace__select">
          <span className="select-measure analytics-workspace__select-measure">All statuses</span>
          <select className="filter-select analytics-workspace__select-input">
            <option value="All statuses">All statuses</option>
            <option value="Approved">Approved</option>
            <option value="Objection">Objection</option>
            <option value="Resolved">Resolved</option>
          </select>
        </div>
        <div className="select-wrap analytics-workspace__select">
          <span className="select-measure analytics-workspace__select-measure">All filing types</span>
          <select className="filter-select analytics-workspace__select-input">
            <option value="All filing types">All filing types</option>
            <option value="Commercial">Commercial</option>
            <option value="Actuarial">Actuarial</option>
            <option value="Homeowners">Homeowners</option>
            <option value="Property">Property</option>
          </select>
        </div>
        <div className="select-wrap analytics-workspace__select">
          <span className="select-measure analytics-workspace__select-measure">All TOI</span>
          <select className="filter-select analytics-workspace__select-input">
            <option value="All TOI">All TOI</option>
            <option value="Homeowners">Homeowners</option>
            <option value="Commercial Property">Commercial Property</option>
          </select>
        </div>
        <div className="select-wrap analytics-workspace__select">
          <span className="select-measure analytics-workspace__select-measure">All companies</span>
          <select className="filter-select analytics-workspace__select-input">
            <option value="All companies">All companies</option>
            <option value="Acuity Mutual">Acuity Mutual</option>
            <option value="Pacific Life">Pacific Life</option>
            <option value="State Farm">State Farm</option>
          </select>
        </div>
        <button className="btn btn-primary analytics-workspace__simulate-btn" disabled={simulating} onClick={() => {
          setSimulating(true);
          setTimeout(() => {
            setSimulating(false);
            setShowResults(true);
          }, 1000);
        }}>
          {simulating ? 'Generating Report...' : 'Run Report'}
        </button>
      </div>

      {!showResults ? <div className="page-empty page-empty--card">
        <div style={{
          background: template.badgeBg,
          color: template.badgeColor
        }} className="page-empty__icon">
          {renderIcon(template.icon, 32, template.badgeColor)}
        </div>
        <h3 className="page-empty__title">Report Ready</h3>
        <p className="page-empty__text">
          Configure your parameters above and click "Run Report" to generate predictive insights, objection clusters, and state metrics.
        </p>
      </div> : <>
        {/* Main Chart Graphic & Insights */}
        <div className="report-card report-results-grid">
          <div>
            <div className="report-card-title report-results-grid__chart-title">
              {template.label} Performance Waveform
            </div>
            <svg width="100%" height="240" viewBox="0 0 800 240" className="report-results-grid__chart">
              {/* Grid lines */}
              <line x1="0" y1="40" x2="800" y2="40" stroke="rgba(0,0,0,0.06)" strokeWidth="1" strokeDasharray="3 3" />
              <line x1="0" y1="120" x2="800" y2="120" stroke="rgba(0,0,0,0.06)" strokeWidth="1" strokeDasharray="3 3" />
              <line x1="0" y1="200" x2="800" y2="200" stroke="rgba(0,0,0,0.1)" strokeWidth="1" />

              {/* Waveform */}
              <path d="M0,160 Q100,40 200,120 T400,60 T600,180 T800,80" fill="none" stroke={template.badgeColor} strokeWidth="4" strokeLinecap="round" />
              <circle cx="200" cy="120" r="6" fill={template.badgeColor} />
              <circle cx="400" cy="60" r="6" fill="var(--c-black)" />
              <circle cx="600" cy="180" r="6" fill="var(--text2)" />

              {/* X-axis labels */}
              <text x="50" y="225" fill="var(--text2)" fontSize="11" fontWeight="600">PHASE 1</text>
              <text x="250" y="225" fill="var(--text2)" fontSize="11" fontWeight="600">PHASE 2</text>
              <text x="450" y="225" fill="var(--text2)" fontSize="11" fontWeight="600">PHASE 3</text>
              <text x="650" y="225" fill="var(--text2)" fontSize="11" fontWeight="600">PHASE 4</text>
            </svg>
          </div>
          <div className="report-results-grid__insights">
            <div className="report-card-title report-results-grid__insights-title">
              Key Insights
            </div>
            <ul className="insight-list">
              <li className="insight-list__item">
                <span style={{
                  backgroundColor: template.badgeColor
                }} className="insight-list__bullet"></span>
                <div><strong>Projected Speed (18.4d):</strong> {template.sub} analysis indicates lead times are 3.2 days faster than the state average.</div>
              </li>
              <li className="insight-list__item">
                <span className="insight-list__bullet insight-list__bullet--dark"></span>
                <div><strong>Objection Probability (14.2%):</strong> Low risk classification achieved through optimized pre-scanned regulatory terms.</div>
              </li>
              <li className="insight-list__item">
                <span className="insight-list__bullet insight-list__bullet--muted"></span>
                <div><strong>Revenue Impact (+$4.82M):</strong> Positive simulated margin gain based directly on competitor price adjustments in Phase 3.</div>
              </li>
            </ul>
          </div>
        </div>
      </>}
    </div>;
  };
  return <>
    <div className="topbar">
      <div className="topbar-left">
        <div className="topbar-title">
          {selectedId === '' ? 'Analytics' : templates.find(t => t.id === selectedId)?.label || 'Analytics'}
        </div>
        <div className="topbar-sub">
          {selectedId === '' ? 'Select a high-fidelity template to perform deep-dive analysis, predict state regulatory actions, and compare filings.' : 'Interactive Sandbox Simulation Workspace'}
        </div>
      </div>
    </div>

    <div className="body analytics-page-body analytics-page-body">
      {selectedId === '' ? (/* --- 4x2 GRID OF TEMPLATES (START SCREEN) --- */
        <div>
          <div className="template-grid">
            {templates.map(t => <div key={t.id} onClick={() => {
              setSelectedId(t.id);
              setShowResults(false);
              setSimulating(false);
            }} onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.05)';
              e.currentTarget.style.borderColor = t.badgeColor;
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.02)';
              e.currentTarget.style.borderColor = 'rgba(0,0,0,0.06)';
            }} className="template-card">
              <div>
                <div className="template-card__header">
                  <span style={{
                    background: t.badgeBg,
                    color: t.badgeColor
                  }} className="template-card__icon-wrap">
                    {renderIcon(t.icon, 18, t.badgeColor)}
                  </span>
                  <span style={{
                    color: t.badgeColor,
                    background: t.badgeBg
                  }} className="template-card__badge">
                    {t.badge}
                  </span>
                </div>
                <h3 className="template-card__title">{t.label}</h3>
                <p className="template-card__sub">{t.sub}</p>
                <p className="template-card__desc">{t.desc}</p>
              </div>
              <div className="template-card__footer">
                {renderMicroGraphic(t.graphicType, t.badgeColor)}
                <span style={{
                  color: t.badgeColor
                }} className="template-card__cta">
                  Open Template <ChevronRight size={12} />
                </span>
              </div>
            </div>)}
          </div>
        </div>) : (/* --- SELECTED WORKSPACE VIEW --- */
        <div>
          <div className="analytics-back-row">
            <button onClick={() => {
              setSelectedId('');
              setReport(null);
              setError('');
              setShowResults(false);
              setSimulating(false);
            }} onMouseEnter={e => e.currentTarget.style.background = 'var(--c-white)'} onMouseLeave={e => e.currentTarget.style.background = 'var(--c-white)'} className="analytics-back-btn">
              <ArrowLeft size={14} /> Back to Templates Library
            </button>
          </div>

          {selectedId === 'competitive_intelligence' ? (/* --- LIVE COMPETITIVE INTELLIGENCE WORKSPACE --- */
            <div>
              <div className="no-print analytics-toolbar analytics-toolbar-panel">
                <div className="analytics-toolbar-grid analytics-toolbar-panel__grid">
                  <div className="analytics-toolbar-panel__field">
                    <label className="df-label analytics-toolbar-panel__label" htmlFor="analytics-competitors-input">
                      Competitors to track
                    </label>
                    <CompetitorChipPicker inputId="analytics-competitors-input" value={competitorTags} onChange={setCompetitorTags} excludeCompanyName={subjectClientName} />
                  </div>
                  <div className="analytics-toolbar-actions analytics-toolbar-panel__actions">
                    <button type="button" className="btn btn-primary analytics-toolbar-panel__run" disabled={loadingReport || !selectedId} onClick={runReport}>
                      {loadingReport ? 'Running Analysis…' : <>Run Report {<ChevronRight size={14} className="chart-thumb-dots__dot0" />}</>}
                    </button>
                  </div>
                </div>

                <p className="analytics-toolbar-hint chart-thumb-dots__dot1">
                  Subject company comes from your linked client. Competitors are substring filters on SERFF company names — search to pick carriers or press Enter to add custom text.
                </p>

                {error ? <div className="chart-thumb-dots__dot2">{error}</div> : null}
              </div>

              <div className="chart-thumb-dots__dot3">
                {report ? <CompetitiveIntelligenceReport report={report} /> : <div className="chart-thumb-dots__dot4">
                  <div className="chart-thumb-dots__dot5">
                    <BarChart3 size={32} />
                  </div>
                  <h3 className="chart-thumb-dots__dot6">Competitive Intelligence Ready</h3>
                  <p className="chart-thumb-dots__dot7">
                    Select your competitor carriers above and click "Run Report" to fetch state approval rates, objection clusters, and trend waves.
                  </p>
                </div>}
              </div>
            </div>) : (/* --- HIGH FIDELITY MOCK WORKSPACES FOR OTHER 7 TEMPLATES --- */
            renderMockWorkspace(selectedId))}
        </div>)}
    </div>
  </>;
};
export default Analytics;